import { just, nothing, type Maybe, matchMaybe } from "./maybe";

/**
 * Single catalog of every `localStorage` key this app writes, plus typed
 * accessors. Device/browser only; not project content and not `.zwlocal`.
 *
 * Call `readBoolPref("outlineCollapsed")` (etc.) directly. Domain helpers below
 * cover per-project outline collapses and JSON shapes that need validation.
 *
 * Early boot in `index.html` duplicates a few string literals (theme, fonts,
 * zoom) before the bundle loads; keep those strings identical to the keys
 * below when changing them.
 */

export const LOCAL_PREF_KEYS = {
  theme: "zephyrwrite-theme",
  leftSidebarWidth: "zephyrwrite-left-sidebar-width",
  rightSidebarWidth: "zephyrwrite-right-sidebar-width",
  uiZoom: "zephyrwrite-ui-zoom",
  outlineCollapsed: "zephyrwrite-outline-collapsed",
  contextPanelCollapsed: "zephyrwrite-context-panel-collapsed",
  databaseGraphView: "zephyrwrite-database-graph-view",
  focusTypewriter: "zephyrwrite-focus-typewriter",
  entityFieldsOpen: "zephyrwrite-entity-fields-open",
  fontBody: "zephyrwrite-font-body",
  fontHeading: "zephyrwrite-font-heading",
  cloudSession: "zw.cloud.session",
  cloudEntitlements: "zw.cloud.entitlements",
  cloudApiProjectIds: "zw.cloud.apiProjectIds",
  cloudUploadsSinceThick: "zw.cloud.uploadsSinceThick",
  betaUpdateDismissed: "zw.beta.updateDismissed",
} as const;

/** Catalog entry name (`"theme"`, `"cloudSession"`, …). */
export type LocalPrefName = keyof typeof LOCAL_PREF_KEYS;

/** On-disk `localStorage` string for a catalog entry. */
export type LocalPrefKey = (typeof LOCAL_PREF_KEYS)[LocalPrefName];

/** Per-project collapse keys (not fixed catalog entries). */
export type ProjectCollapseKey =
  `zw.collapsedChapters:${string}` | `zw.collapsedActs:${string}`;

const COLLAPSED_CHAPTERS_PREFIX = "zw.collapsedChapters:" as const;
const COLLAPSED_ACTS_PREFIX = "zw.collapsedActs:" as const;

export function collapsedChaptersKey(rootPath: string): ProjectCollapseKey {
  return `${COLLAPSED_CHAPTERS_PREFIX}${rootPath}`;
}

export function collapsedActsKey(rootPath: string): ProjectCollapseKey {
  return `${COLLAPSED_ACTS_PREFIX}${rootPath}`;
}

function storageAvailable(): boolean {
  return typeof localStorage !== "undefined";
}

function storageKey(name: LocalPrefName): LocalPrefKey {
  return LOCAL_PREF_KEYS[name];
}

export function readBoolPref(name: LocalPrefName, fallback = false): boolean {
  if (storageAvailable() === false) return fallback;
  const raw = localStorage.getItem(storageKey(name));
  if (raw === null) return fallback;
  return raw === "1";
}

export function writeBoolPref(name: LocalPrefName, value: boolean): void {
  if (storageAvailable() === false) return;
  localStorage.setItem(storageKey(name), value ? "1" : "0");
}

export function readStringPref(name: LocalPrefName): Maybe<string> {
  if (storageAvailable() === false) return nothing;
  const raw = localStorage.getItem(storageKey(name));
  return raw !== null && raw.length > 0 ? just(raw) : nothing;
}

export function writeStringPref(name: LocalPrefName, value: string): void {
  if (storageAvailable() === false) return;
  localStorage.setItem(storageKey(name), value);
}

export function removePref(name: LocalPrefName): void {
  if (storageAvailable() === false) return;
  localStorage.removeItem(storageKey(name));
}

export function readJsonPref<T>(
  name: LocalPrefName,
  validate: (value: unknown) => value is T,
): Maybe<T> {
  return readJsonAtKey(storageKey(name), validate);
}

export function writeJsonPref(name: LocalPrefName, value: unknown): void {
  writeJsonAtKey(storageKey(name), value);
}

function readJsonAtKey<T>(
  key: LocalPrefKey | ProjectCollapseKey,
  validate: (value: unknown) => value is T,
): Maybe<T> {
  if (storageAvailable() === false) return nothing;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null || raw.length === 0) return nothing;
    const parsed: unknown = JSON.parse(raw);
    return validate(parsed) ? just(parsed) : nothing;
  } catch {
    return nothing;
  }
}

function writeJsonAtKey(
  key: LocalPrefKey | ProjectCollapseKey,
  value: unknown,
): void {
  if (storageAvailable() === false) return;
  localStorage.setItem(key, JSON.stringify(value));
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === "string");
}

// --- Per-project outline / Storyboard collapses ---------------------------

export function readCollapsedChapters(rootPath: string): string[] {
  return matchMaybe(
    readJsonAtKey(collapsedChaptersKey(rootPath), isStringArray),
    { nothing: () => [], just: (value) => value },
  );
}

export function writeCollapsedChapters(
  rootPath: string,
  collapsed: string[],
): void {
  writeJsonAtKey(collapsedChaptersKey(rootPath), collapsed);
}

export function readCollapsedActs(rootPath: string): string[] {
  return matchMaybe(readJsonAtKey(collapsedActsKey(rootPath), isStringArray), {
    nothing: () => [],
    just: (value) => value,
  });
}

export function writeCollapsedActs(
  rootPath: string,
  collapsed: string[],
): void {
  writeJsonAtKey(collapsedActsKey(rootPath), collapsed);
}

/** Keep collapse keys in sync when a chapter folder is renamed. */
export function remapCollapsedChapterFolder(
  rootPath: string,
  fromFolder: string,
  toFolder: string,
): string[] {
  const fromPrefix = `Chapters/${fromFolder}/`;
  const toPrefix = `Chapters/${toFolder}/`;
  const next = readCollapsedChapters(rootPath).map((p) =>
    p.startsWith(fromPrefix) ? `${toPrefix}${p.slice(fromPrefix.length)}` : p,
  );
  writeCollapsedChapters(rootPath, next);
  return next;
}

export function removeCollapsedChapterFolder(
  rootPath: string,
  folder: string,
): string[] {
  const prefix = `Chapters/${folder}/`;
  const next = readCollapsedChapters(rootPath).filter(
    (p) => p.startsWith(prefix) === false,
  );
  writeCollapsedChapters(rootPath, next);
  return next;
}

export function remapCollapsedActFolder(
  rootPath: string,
  fromFolder: string,
  toFolder: string,
): string[] {
  const fromPath = `Chapters/${fromFolder}/index.zwdoc`;
  const toPath = `Chapters/${toFolder}/index.zwdoc`;
  const next = readCollapsedActs(rootPath).map((p) =>
    p === fromPath ? toPath : p,
  );
  writeCollapsedActs(rootPath, next);
  return next;
}

export function removeCollapsedActFolder(
  rootPath: string,
  folder: string,
): string[] {
  const path = `Chapters/${folder}/index.zwdoc`;
  const next = readCollapsedActs(rootPath).filter((p) => p !== path);
  writeCollapsedActs(rootPath, next);
  return next;
}
